import { useQuery } from "@tanstack/react-query";
import { Link, useLocation } from "react-router-dom";

import { LogoMark, LogoWordmark } from "@m2s/design-system";

import { useIsMobile } from "@/hooks/useIsMobile";
import { api } from "@/lib/api";
import type { Project } from "@/lib/types";
import { useAuthStore } from "@/store/auth";
import { useUIStore } from "@/store/ui";

type NavItem = {
  icon: string;
  label: string;
  to: string;
  adminOnly?: boolean;
  employeeOnly?: boolean; // shown only to non-admin (employee-role) members
  superAdminOnly?: boolean;
};

/** Active-state match — longest-prefix wins.
 *
 * For each nav item, we check whether the current path is a prefix-match of
 * the item's `to`. The most-specific match (longest target path) is the only
 * one that highlights. That stops `/admin` from co-lighting with
 * `/admin/onboarding` when the user is on the latter — both technically match
 * `/admin`, but `/admin/onboarding` is longer, so it wins.
 *
 * Drill-in routes under the super-admin surface (e.g. `/superadmin/tenants/...`)
 * are remapped to `/superadmin/dashboard` for matching, so the Super-admin
 * nav entry stays lit while you're inside a tenant detail page.
 */
function findActiveNav(currentPath: string, items: NavItem[]): NavItem | null {
  const effective = currentPath.startsWith("/superadmin/tenants")
    ? "/superadmin/dashboard"
    : currentPath;

  let best: { item: NavItem; depth: number } | null = null;
  for (const item of items) {
    const target = item.to;
    if (effective === target || effective.startsWith(target + "/")) {
      const depth = target.split("/").filter(Boolean).length;
      if (!best || depth > best.depth) best = { item, depth };
    }
  }
  return best?.item ?? null;
}

// Tenant-level nav. Visible to anyone who has a tenant membership in this
// workspace and isn't browsing the super-admin surface. `adminOnly: true`
// further restricts to admin-role members.
const TENANT_NAV: NavItem[] = [
  { icon: "■", label: "Board", to: "/board" },
  { icon: "☑", label: "My board", to: "/my-board", employeeOnly: true },
  { icon: "▤", label: "Docs", to: "/wiki" },
  { icon: "◷", label: "Timesheet", to: "/time" },
  { icon: "◫", label: "Rooms", to: "/rooms", employeeOnly: true },
  { icon: "◐", label: "My progress", to: "/me/progress" },
  { icon: "◉", label: "Members", to: "/members", adminOnly: true },
  { icon: "◈", label: "Admin dashboard", to: "/admin", adminOnly: true },
  { icon: "◷", label: "Timesheet dashboard", to: "/admin/timesheets", adminOnly: true },
  { icon: "✎", label: "Fill timesheet", to: "/admin/fill-timesheet", adminOnly: true },
  { icon: "▣", label: "Approvals", to: "/approvals", adminOnly: true },
  { icon: "✚", label: "Employee management", to: "/admin/onboarding", adminOnly: true },
  { icon: "⚙", label: "Settings", to: "/settings" },
];

// Super-admin nav. Cross-tenant only — these users are SaaS owners, not
// consultancy employees. Tenant-level surfaces stay hidden until they drill
// into a specific tenant via /superadmin/tenants/{slug}.
//
// Mirrors the tenant-admin shape (Admin dashboard / Employee onboarding /
// Settings) so the structure is obvious to anyone familiar with the
// per-tenant nav: top item is the data view, second is the onboarding
// form, last is Settings.
const SUPER_ADMIN_NAV: NavItem[] = [
  { icon: "★", label: "Super admin", to: "/superadmin/dashboard" },
  { icon: "✚", label: "Tenant management", to: "/superadmin/onboarding" },
  { icon: "▦", label: "Create project", to: "/superadmin/projects" },
  { icon: "⊞", label: "Manage projects", to: "/superadmin/assign" },
  { icon: "◧", label: "Existing projects", to: "/superadmin/progress" },
  { icon: "⊘", label: "Atlas errors", to: "/superadmin/atlas-errors" },
  { icon: "◔", label: "Atlas analytics", to: "/superadmin/atlas-analytics" },
  { icon: "◎", label: "Atlas call traces", to: "/superadmin/atlas-traces" },
  { icon: "⚙", label: "Settings", to: "/settings" },
];

// Tenant-role gating — only `admin` unlocks admin-only items. `employee` sees
// just the everyday work items.
const ADMIN_ROLES = new Set(["admin"]);

export function Sidebar() {
  const collapsed = useUIStore((s) => s.sidebarCollapsed);
  const toggle = useUIStore((s) => s.toggleSidebar);
  const mobileNavOpen = useUIStore((s) => s.mobileNavOpen);
  const setMobileNavOpen = useUIStore((s) => s.setMobileNavOpen);
  const memberships = useAuthStore((s) => s.memberships);
  const isSuperAdmin = useAuthStore((s) => s.isSuperAdmin);
  const location = useLocation();
  const isMobile = useIsMobile();

  // On mobile the sidebar is always full-width (240px) — collapsing makes no
  // sense for a drawer that's either open or closed. Slide it off-screen
  // when the drawer is closed.
  const effectiveCollapsed = isMobile ? false : collapsed;
  const sidebarWidth = effectiveCollapsed ? 56 : 240;

  // Super admins ALWAYS see only the super-admin nav — Super admin (which
  // wraps tenant-list + tenant onboarding) and Settings. Tenant-level surfaces
  // (Board, Wiki, Time, Members, Admin dashboard, Employee onboarding,
  // Projects) belong to tenant admins and employees, not to the SaaS owners.
  // If a super admin needs to inspect a tenant's data, they drill in via
  // /superadmin/tenants/{slug}.
  const isAdmin = memberships.some((m) => ADMIN_ROLES.has(m.role));

  const navItems = isSuperAdmin
    ? SUPER_ADMIN_NAV
    : TENANT_NAV.filter((n) => {
        if (n.adminOnly) return isAdmin;
        if (n.employeeOnly) return !isAdmin; // Rooms — employees only for now
        return true;
      });

  const { data: projects } = useQuery({
    queryKey: ["projects"],
    queryFn: async () => (await api.get<Project[]>("/projects")).data,
    enabled: !isSuperAdmin,
  });

  return (
    <aside
      style={{
        position: "fixed",
        left: 0,
        top: 0,
        bottom: 0,
        width: sidebarWidth,
        background: "var(--surface-1)",
        borderRight: "1px solid var(--hairline)",
        display: "flex",
        flexDirection: "column",
        transition: "width 120ms ease-out, transform 180ms ease-out",
        zIndex: 10,
        // Slide off-screen on mobile unless the drawer is open.
        transform:
          isMobile && !mobileNavOpen ? "translateX(-100%)" : "translateX(0)",
        boxShadow:
          isMobile && mobileNavOpen ? "0 8px 32px rgba(8,8,8,0.18)" : "none",
      }}
    >
      <div
        style={{
          padding: effectiveCollapsed ? "16px 0" : "16px 20px",
          display: "flex",
          alignItems: "center",
          gap: 10,
          borderBottom: "1px solid var(--hairline)",
          justifyContent: effectiveCollapsed ? "center" : "flex-start",
        }}
      >
        <Link
          to="/"
          aria-label="minute2sec home"
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 10,
            textDecoration: "none",
          }}
        >
          <LogoMark size={24} />
          {!effectiveCollapsed && <LogoWordmark size={15} />}
        </Link>
      </div>

      <nav style={{ flex: 1, padding: effectiveCollapsed ? "8px 4px" : "8px 12px", overflowY: "auto" }}>
        {(() => {
          const activeItem = findActiveNav(location.pathname, navItems);
          return navItems.map((item) => {
            const active = item === activeItem;
          return (
            <Link
              key={item.label}
              to={item.to}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                // Larger touch target on mobile (44pt is the Apple HIG minimum).
                padding: effectiveCollapsed ? "8px" : isMobile ? "12px 12px" : "7px 10px",
                marginBottom: 2,
                color: active ? "var(--ink)" : "var(--body-mid)",
                background: active ? "var(--surface-2)" : "transparent",
                borderRadius: 4,
                fontSize: 13,
                fontWeight: active ? 500 : 400,
                textDecoration: "none",
                justifyContent: effectiveCollapsed ? "center" : "flex-start",
              }}
            >
              <span style={{ width: 16, textAlign: "center", color: "var(--mute)" }}>
                {item.icon}
              </span>
              {!effectiveCollapsed && <span>{item.label}</span>}
            </Link>
          );
          });
        })()}

        {!effectiveCollapsed && !isSuperAdmin && projects && projects.length > 0 && (
          <>
            <div
              style={{
                fontSize: 10,
                fontWeight: 500,
                letterSpacing: 1.2,
                textTransform: "uppercase",
                color: "var(--mute)",
                margin: "20px 10px 8px",
              }}
            >
              Projects
            </div>
            {projects.map((p) => {
              const active = location.pathname === `/board/${p.key}`;
              return (
                <Link
                  key={p.key}
                  to={`/board/${p.key}`}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    padding: "6px 10px",
                    marginBottom: 1,
                    color: active ? "var(--ink)" : "var(--body-mid)",
                    background: active ? "var(--surface-2)" : "transparent",
                    borderRadius: 4,
                    fontSize: 12.5,
                    textDecoration: "none",
                  }}
                >
                  <span
                    aria-hidden
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: 2,
                      background: p.color,
                      flexShrink: 0,
                    }}
                  />
                  <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis" }}>
                    {p.name}
                  </span>
                  <span style={{ fontFamily: "var(--font-mono)", fontSize: 10, color: "var(--mute)" }}>
                    {p.key}
                  </span>
                </Link>
              );
            })}
          </>
        )}
      </nav>

      {/* Desktop-only collapse toggle. On mobile the drawer is binary
          (open/closed), so the toggle doesn't apply. Instead we offer a
          Close button so users can dismiss without tapping the backdrop. */}
      <button
        type="button"
        onClick={() => (isMobile ? setMobileNavOpen(false) : toggle())}
        style={{
          padding: 12,
          background: "transparent",
          border: "none",
          borderTop: "1px solid var(--hairline)",
          color: "var(--mute)",
          fontSize: 11,
          fontFamily: "var(--font-mono)",
          textAlign: "left",
          cursor: "pointer",
        }}
      >
        {isMobile ? "✕ close" : effectiveCollapsed ? "›" : "‹ collapse"}
      </button>
    </aside>
  );
}
